import React, { useState, useEffect } from 'react';
import { Card, Table, DatePicker, Form, Row, Col, Select, message, Space } from 'antd';
import axios from '../services/axiosConfig';
import moment from 'moment';
import { USER_API } from '../constants/api';

const UserActivityLogs = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [form] = Form.useForm();

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
      width: 180
    },
    {
      title: 'Người dùng',
      dataIndex: 'User',
      key: 'User',
      width: 200,
      render: (user) => user ? `${user.fullName} (${user.username})` : '-'
    },
    {
      title: 'Hành động',
      dataIndex: 'ActionType',
      key: 'ActionType',
      width: 150,
      render: (text) => {
        switch(text) {
          case 'create':
            return 'Tạo mới';
          case 'update':
            return 'Cập nhật';
          case 'delete':
            return 'Xóa';
          case 'view':
            return 'Xem';
          case 'login':
            return 'Đăng nhập';
          case 'logout':
            return 'Đăng xuất';
          default:
            return text;
        }
      }
    },
    {
      title: 'Đối tượng',
      dataIndex: 'TargetType',
      key: 'TargetType',
      width: 150,
      render: (text) => {
        switch(text) {
          case 'user':
            return 'Người dùng';
          case 'tenant':
            return 'Khách thuê';
          case 'contract':
            return 'Hợp đồng';
          case 'invoice':
            return 'Hóa đơn';
          case 'payment':
            return 'Thanh toán';
          case 'water-bill':
            return 'Hóa đơn nước';
          case 'electricity-bill':
            return 'Hóa đơn điện';
          case 'report':
            return 'Báo cáo';
          case 'factory':
            return 'Nhà xưởng';
          case 'rent-invoice':
            return 'Hóa đơn thuê';
          case 'rent':
            return 'Hóa đơn';
          case 'deposit':
            return 'Tiền cọc';

          case 'price-adjustment':
            return 'Điều chỉnh giá';
          case 'overdue-invoice': 
            return 'Hóa đơn quá hạn';
          case 'message':
            return 'Tin nhắn';
          case 'support':
            return 'Hỗ trợ';
          default:
            return text;
        }
      }
    },
    {
      title: 'Chi tiết',
      dataIndex: 'ActionDetail',
      key: 'ActionDetail',
      ellipsis: true
    },
    {
      title: 'IP',
      dataIndex: 'IPAddress',
      key: 'IPAddress',
      width: 130
    }
  ];

  const fetchData = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page,
        limit: pageSize,
        startDate: values.startDate?.format('YYYY-MM-DD'),
        endDate: values.endDate?.format('YYYY-MM-DD'),
        actionType: values.actionType,
        targetType: values.targetType
      };

      const response = await axios.get(USER_API.ACTIVITY_LOGS, { params });
      setData(response.data.data);
      setPagination({
        current: response.data.pagination.page,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.total
      });
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      message.error('Không thể tải nhật ký hoạt động');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData(1, pagination.pageSize);
  };

  const handleTableChange = (paginationInfo) => {
    fetchData(paginationInfo.current, paginationInfo.pageSize);
  };

  const actionOptions = [
    { value: 'create', label: 'Tạo mới' },
    { value: 'update', label: 'Cập nhật' },
    { value: 'delete', label: 'Xóa' },
    { value: 'view', label: 'Xem' },
    { value: 'login', label: 'Đăng nhập' },
    { value: 'logout', label: 'Đăng xuất' },
    { value: 'change_password', label: 'Đổi mật khẩu' },
  ];

  const targetTypeOptions = [
    { value: 'user', label: 'Người dùng' },
    { value: 'tenant', label: 'Khách thuê' },
    { value: 'contract', label: 'Hợp đồng' },
    { value: 'invoice', label: 'Hóa đơn' },
    { value: 'payment', label: 'Thanh toán' },
    { value: 'role', label: 'Vai trò' },
    { value: 'permission', label: 'Phân quyền' },
    { value: 'feedback', label: 'Phản hồi' },
    { value: 'notification', label: 'Thông báo' },
    { value: 'water-bill', label: 'Hóa đơn nước' },
    { value: 'electricity-bill', label: 'Hóa đơn điện' },
    { value: 'report', label: 'Báo cáo' },
    { value: 'factory', label: 'Nhà xưởng' },

    { value: 'price-adjustment', label: 'Điều chỉnh giá' },
    { value: 'overdue-invoice', label: 'Hóa đơn quá hạn' },
    { value: 'message', label: 'Tin nhắn' },
    { value: 'support', label: 'Hỗ trợ' },
    { value: 'deposit', label: 'Tiền cọc' },
    { value: 'rent-invoice', label: 'Hóa đơn thuê' },
    { value: 'rent', label: 'Hóa đơn' },
    { value: 'other', label: 'Khác' }
  ];

  return (
    <Card title="Nhật ký hoạt động">
      <Form form={form} onFinish={handleSearch}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="startDate" label="Từ ngày">
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="endDate" label="Đến ngày">
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="actionType" label="Hành động">
              <Select
                allowClear
                placeholder="Chọn hành động"
                options={actionOptions}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="targetType" label="Đối tượng">
              <Select
                allowClear
                placeholder="Chọn đối tượng"
                options={targetTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <button type="button" style={{ backgroundColor: '#1890ff', color: 'white', border: 'none',padding:5,borderRadius:5 }} onClick={() => form.resetFields()}>
                Xóa bộ lọc
              </button>
              <button type="submit" className="ant-btn ant-btn-primary" style={{ backgroundColor: '#1890ff', color: 'white', border: 'none', padding:5,borderRadius:5 }}>
                Tìm kiếm
              </button>
            </Space>
          </Col>
        </Row>
      </Form>

      <Table
        loading={loading}
        dataSource={data}
        columns={columns}
        rowKey="ActivityID"
        scroll={{ x: 1200 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Tổng số ${total} bản ghi`,
          showQuickJumper: true
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default UserActivityLogs; 